<template>
    <div>
        <v-col cols="12">
            <h2>
                {{ id ? t('error-pages::page.title.edit_error_page') : t('error-pages::page.title.new_error_page') }}
            </h2>
        </v-col>
        <v-col cols="12">
            <div class="form-wrapper">
                <v-form ref="form" v-model="isFormValid">
                    <v-container>
                        <v-row>
                            <v-col cols="12">
                                <v-text-field
                                    v-model="error_page.file_name"
                                    dense
                                    rounded
                                    outlined
                                    background-color="#fff"
                                    :label="`${$root.defaultMandatoryAsteriks} ${t(
                                        'error-pages::edit.placeholder.file_name'
                                    )}`"
                                    :rules="[
                                        (v) => !!v || t('default::validation.field.required'),
                                        (v) =>
                                            (v || '').indexOf(' ') < 0 ||
                                            t('default::validation.field.not_allowed_spaces'),
                                        (v) => !names.includes(v) || t('default::validation.field.unique'),
                                    ]"
                                >
                                </v-text-field>
                                <v-row>
                                    <v-col cols="12" lg="8" class="pb-0 pr-lg-10">
                                        <v-autocomplete
                                            v-model="error_page.http_status"
                                            :label="`${$root.defaultMandatoryAsteriks} ${t(
                                                'error-pages::edit.placeholder.http_status'
                                            )}`"
                                            :items="http_statuses"
                                            background-color="#fff"
                                            item-text="text"
                                            item-value="value"
                                            :rules="[(v) => !!v || t('default::validation.field.required')]"
                                            clearable
                                            dense
                                            append-icon="mdi-chevron-down"
                                            outlined
                                            rounded
                                            required
                                        >
                                        </v-autocomplete>
                                    </v-col>
                                    <v-col cols="12" lg="4" class="pb-0 pl-lg-10">
                                        <v-switch
                                            v-model="error_page.is_active"
                                            class="mt-0"
                                            :label="
                                                error_page.is_active
                                                    ? t('error-pages::edit.label.active')
                                                    : t('error-pages::edit.label.inactive')
                                            "
                                            :true-value="true"
                                            :false-value="false"
                                        >
                                        </v-switch>
                                    </v-col>
                                </v-row>
                                <v-row>
                                    <v-col cols="12" class="pb-0">
                                        <b-alert :message="httpStatusHint"></b-alert>
                                    </v-col>
                                </v-row>
                                <v-row>
                                    <v-col
                                        :class="{ 'editor-invalid': errorMessages.file_content.length > 0 }"
                                        cols="12"
                                        md="12"
                                        lg="12"
                                        sm="12"
                                    >
                                        <v-label>* {{ t('email-templates::details.label.html_content') }}</v-label>
                                        <b-text-editor
                                            v-model="error_page.file_content"
                                            :api-key="tinymceApiKey"
                                            :def-plugins="[]"
                                        >
                                        </b-text-editor>
                                        <div
                                            v-if="errorMessages.file_content.length > 0"
                                            class="v-text-field__details mt-1 ml-3"
                                        >
                                            <div class="v-messages theme--light error--text" role="alert">
                                                <div class="v-messages__wrapper">
                                                    <div class="v-messages__message">
                                                        {{ t('default::validation.field.required') }}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </v-col>
                                </v-row>
                            </v-col>
                        </v-row>
                    </v-container>
                    <b-form-actions
                        :id="id"
                        :back-route="$root.route('error-pages.index')"
                        @discard="reset"
                        @save="save"
                    ></b-form-actions>
                </v-form>
            </div>
        </v-col>
    </div>
</template>

<script>
let formSubmitMixin = require(process.env.MIX_APP_ROOT +
    '/bgo/bgo-components/resources/js/mixins/FormSubmit.js').default;

export default {
    name: 'ErrorPageFormComponent',
    mixins: [formSubmitMixin],
    props: {
        id: {
            type: String,
            required: true,
        },
        tinymceApiKey: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            loading: true,
            saveLoading: false,
            isFormValid: false,
            lockSelection: false,
            error_page: {
                file_name: null,
                is_active: true,
                http_status: null,
                file_content: null,
            },
            statuses: [],
            httpStatusHint: t('error-pages::edit.hint.http_status'),
            messageTemplate: {},
            errorMessages: {
                file_content: [],
            },
            http_statuses: [
                { header: '1xx informational response' },
                { text: '100 Continue', value: '100' },
                { text: '101 Switching Protocols', value: '101' },
                { text: '102 Processing', value: '102' },
                { text: '103 Early Hints', value: '103' },
                { text: '103 Checkpoint', value: '103' },
                { divider: true },
                { header: '2xx successful' },
                { text: '200 OK', value: '200' },
                { text: '201 Created', value: '201' },
                { text: '202 Accepted', value: '202' },
                { text: '203 Non-Authoritative Information (since HTTP/1.1)', value: '203' },
                { text: '204 No Content', value: '204' },
                { text: '205 Reset Content', value: '205' },
                { text: '206 Partial Content', value: '206' },
                { text: '207 Multi-Status', value: '207' },
                { text: '208 Already Reported', value: '208' },
                { text: '218 This is fine (Apache Web Server)', value: '218' },
                { text: '226 IM Used', value: '226' },
                { divider: true },
                { header: '3xx redirection' },
                { text: '300 Multiple Choices', value: '300' },
                { text: '301 Moved Permanently', value: '301' },
                { text: '302 Found (Previously "Moved temporarily")', value: '302' },
                { text: '303 See Other (since HTTP/1.1)', value: '303' },
                { text: '304 Not Modified', value: '304' },
                { text: '305 Use Proxy (since HTTP/1.1)', value: '305' },
                { text: '306 Switch Proxy', value: '306' },
                { text: '307 Temporary Redirect (since HTTP/1.1)', value: '307' },
                { text: '308 Permanent Redirect', value: '308' },
                { divider: true },
                { header: '4xx client error' },
                { text: '400 Bad Request', value: '400' },
                { text: '401 Unauthorized', value: '401' },
                { text: '402 Payment Required', value: '402d' },
                { text: '403 Forbidden', value: '403' },
                { text: '404 Not Found', value: '404' },
                { text: '405 Method Not Allowed', value: '405' },
                { text: '406 Not Acceptable', value: '406' },
                { text: '407 Proxy Authentication Required', value: '407' },
                { text: '408 Request Timeout', value: '408' },
                { text: '409 Conflict', value: '409' },
                { text: '410 Gone', value: '410' },
                { text: '411 Length Required', value: '411' },
                { text: '412 Precondition Failed', value: '412' },
                { text: '413 Payload Too Large', value: '413' },
                { text: '414 URI Too Long', value: '414' },
                { text: '415 Unsupported Media Type', value: '415' },
                { text: '416 Range Not Satisfiable', value: '416' },
                { text: '417 Expectation Failed', value: '417' },
                { text: '418 I am a teapot', value: '418t' },
                { text: '419 Page Expired (Laravel Framework)', value: '419' },
                { text: '421 Misdirected Request', value: '421' },
                { text: '422 Unprocessable Entity', value: '422' },
                { text: '423 Locked', value: '423' },
                { text: '424 Failed Dependency', value: '424' },
                { text: '425 Too Early', value: '425' },
                { text: '426 Upgrade Required', value: '426' },
                { text: '428 Precondition Required', value: '428' },
                { text: '429 Too Many Requests', value: '429' },
                { text: '431 Request Header Fields Too Large', value: '431' },
                { text: '444 No Response - nginx', value: '444' },
                { text: '451 Unavailable For Legal Reasons', value: '451s' },
                {
                    text: '460 AWS Elastic Load Balancer - Typically when client timeout is sooner than the Elastic Load Balancer is timeout.',
                    value: '460',
                },
                {
                    text: '463 AWS Elastic Load Balancer - The load balancer received an X-Forwarded-For request header with more than 30 IP addresses.',
                    value: '463',
                },
                { text: '494 Request header too large - nginx', value: '494' },
                { text: '495 SSL Certificate Error - nginx', value: '495' },
                { text: '496 SSL Certificate Required - nginx', value: '496x' },
                { text: '497 HTTP Request Sent to HTTPS Port - nginx', value: '497' },
                { text: '498 Invalid Token (Esri)', value: '498' },
                { text: '499 Token Required (Esri)', value: '499' },
                { text: '499 Client Closed Request - nginx', value: '499' },
                { divider: true },
                { header: '5xx server error' },
                { text: '500 Internal Server Error', value: '500' },
                { text: '501 Not Implemented', value: '501' },
                { text: '502 Bad Gateway', value: '502' },
                { text: '503 Service Unavailable', value: '503' },
                { text: '504 Gateway Timeout', value: '504' },
                { text: '505 HTTP Version Not Supported', value: '505' },
                { text: '506 Variant Also Negotiates', value: '506' },
                { text: '507 Insufficient Storage', value: '507' },
                { text: '508 Loop Detected', value: '508' },
                { text: '509 Bandwidth Limit Exceeded (Apache Web Server/cPanel)', value: '509' },
                { text: '510 Not Extended', value: '510' },
                { text: '511 Network Authentication Required', value: '511' },
                { text: '526 Invalid SSL Certificate', value: '526' },
                { text: '529 Site is overloaded', value: '529' },
                { text: '530 Site is frozen', value: '530' },
                { text: '598 (Informal convention) Network read timeout error', value: '598' },
                { divider: true },
            ],
            names: [],
        };
    },
    created() {
        this.loadErrorPage();
        this.loadErrorPageNames();
    },
    methods: {
        loadErrorPage() {
            if (_.isEmpty(this.id)) {
                this.loading = false;
                return;
            }
            axios
                .get(route('api.v1.error-pages.show', this.id))
                .then((response) => {
                    this.error_page = response.data.data;
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        loadErrorPageNames() {
            var id = this.id;
            axios
                .get(route('api.v1.error-pages.index'), {
                    params: {
                        fields: 'id,file_name',
                        error_page: 0,
                    },
                })
                .then((response) => {
                    let names = [];

                    _.each(response.data.data, function (record) {
                        if (!id && id !== record.id) {
                            names.push(record.file_name);
                        }
                    });

                    this.names = names;
                });
        },
        reset() {
            this.route = _.clone(this.routeOriginal);
        },
        save() {
            if (this.error_page.file_content === null) {
                this.errorMessages.file_content.push(t('default::validation.field.required'));
            }

            if (this.id) {
                this.submit(
                    route('api.v1.error-pages.update', {
                        error_page: this.id,
                    }),
                    this.error_page,
                    'put',
                    () => {
                        this.$root.notify('info', t('default::notification.saved'));
                        this.$root.goTo(route('error-pages.index'));
                    }
                );
            } else {
                this.submit(route('api.v1.error-pages.store'), this.error_page, 'post', () => {
                    this.$root.notify('info', t('default::notification.saved'));
                    this.$root.goTo(route('error-pages.index'));
                });
            }
        },
    },
};
</script>
<style scoped></style>
